import { useMutation } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { Card, SwitchInput, useToast } from 'corso-admin-ui';
import { type CommandAdminApi } from 'corso-types';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import {
  useRefetchStore,
  useStoreContext,
  useStoreId,
} from '~/providers/StoreProvider';

type ShippingLine = CommandAdminApi.ResponseBody<
  '/store/:storeId',
  'get'
>['shippingLines'][number];

const { idColumn } = sharedTableDefinitions;

function useUpdateShippingLine() {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();
  return useMutation({
    mutationFn: ({
      shippingLineId,
      body,
    }: {
      shippingLineId: number;
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/shipping-line/:shippingLineId',
        'put'
      >;
    }) => api.store(storeId).updateShippingLine(shippingLineId, body),
    onSuccess: () => {
      refetch();
      toast.show('Updated shipping line');
    },
    onError: () => toast.show('Failed to update shipping line'),
  });
}

function ReorderToggle({ shippingLine }: { shippingLine: ShippingLine }) {
  const { mutate, isPending } = useUpdateShippingLine();
  return (
    <SwitchInput
      id={`shipping-line-reorder-${shippingLine.id}`}
      label="Allow reorder"
      labelVisuallyHidden
      checked={shippingLine.allowReorder}
      disabled={isPending}
      onChange={(event) =>
        mutate({
          shippingLineId: shippingLine.id,
          body: { allowReorder: event.target.checked },
        })
      }
    />
  );
}

const shippingLineColumns: ColumnDef<ShippingLine>[] = [
  idColumn<ShippingLine>({ meta: { align: 'left' } }),
  {
    accessorKey: 'name',
    header: 'Name',
    enableSorting: true,
    cell: ({ row }) => row.original.name ?? '—',
  },
  {
    accessorKey: 'allowReorder',
    meta: {
      align: 'right',
    },
    header: 'Allow Reorder',
    cell: ({ row }) => <ReorderToggle shippingLine={row.original} />,
  },
];

export default function ShippingLinesCard() {
  const { shippingLines } = useStoreContext();

  return (
    <Card>
      <Card.Heading>Reorder Shipping Lines</Card.Heading>
      {shippingLines.length > 0 ? (
        <DataTable
          data={shippingLines}
          columns={shippingLineColumns}
          filterable
          pageSize={10}
        />
      ) : (
        <DataTableStatus
          icon="shipment"
          title="No shipping lines"
          details="Shipping lines will appear here once they sync from the store."
        />
      )}
    </Card>
  );
}
